// src/pages/DashboardPage.jsx
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Crown,
  LayoutDashboard,
  Users,
  GraduationCap,
  Library,
  ClipboardList,
  BookOpenCheck,
  User,
  Book,
  ChevronRight
} from 'lucide-react'
import { LayoutContext } from '@/layout/KuizyLayout'
import {
  getTests,
  getMyCourses,
  getCourses,
  getClasses,
  getGroups,
  getLearners,
  getAdminUsers,
  getSubjects,
  fetchExamBank
} from '@/api/kuizy'

const fadeIn = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.35 } }
const isUUID = s => typeof s === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s)
const pick = r => r?.data?.rows || r?.data || []
const resolveTotal = d => (d?.total ?? d?.total_rows ?? d?.count ?? d?.totalCount ?? (Array.isArray(d?.rows) ? d.rows.length : (Array.isArray(d) ? d.length : 0)))

function StatCard({ icon: Icon, label, value }) {
  return (
    <motion.div {...fadeIn} className="rounded-2xl bg-gradient-to-br from-white to-gray-50 border border-gray-200 shadow p-5">
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-xl bg-primary/10 text-primary">
          <Icon size={22} />
        </div>
        <div>
          <div className="text-sm text-gray-500">{label}</div>
          <div className="text-2xl font-bold text-gray-800">{Number(value ?? 0).toLocaleString()}</div>
        </div>
      </div>
    </motion.div>
  )
}

function ListCard({ icon: Icon, title, items, empty, onViewAll, renderItem }) {
  return (
    <motion.div {...fadeIn} className="rounded-2xl bg-white border border-gray-100 shadow overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-primary/10 text-primary"><Icon size={18} /></div>
          <div className="font-semibold text-gray-800">{title}</div>
        </div>
        {onViewAll && (
          <button onClick={onViewAll} className="text-primary hover:underline text-sm flex items-center gap-1">
            ดูทั้งหมด <ChevronRight size={16} />
          </button>
        )}
      </div>
      <div className="divide-y divide-gray-100">
        {items && items.length > 0
          ? items.map((it, i) => <div key={i} className="px-4 py-3">{renderItem(it)}</div>)
          : <div className="px-4 py-8 text-center text-gray-400 text-sm">{empty}</div>}
      </div>
    </motion.div>
  )
}

export default function DashboardPage() {
  const nav = useNavigate()
  const { mode, roles } = useContext(LayoutContext)

  const isAdmin = roles.includes('ADMIN') || roles.includes('MASTER_ADMIN')
  const isMaster = roles.includes('MASTER_ADMIN')
  const isCreator = isAdmin || roles.includes('CREATOR') || mode === 'user'
  const [activeTab, setActiveTab] = useState(isAdmin ? 'overview' : 'mycontent')

  const [tests, setTests] = useState([])
  const [courses, setCourses] = useState([])
  const [classes, setClasses] = useState([])
  const [groups, setGroups] = useState([])
  const [learners, setLearners] = useState([])
  const [admins, setAdmins] = useState([])
  const [myCourses, setMyCourses] = useState([])
  const [subjectsCount, setSubjectsCount] = useState(0)
  const [examBankTotal, setExamBankTotal] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    async function load() {
      setLoading(true)
      const isLearner = localStorage.getItem('learner') === '1'
      try {
        const adminJobs = {}
        const creatorJobs = {}

        if (isAdmin) {
          adminJobs.tests = getTests({}).then(pick).catch(() => [])
          adminJobs.courses = getCourses({ status: 'active' }).then(pick).catch(() => [])
          adminJobs.classes = getClasses().then(pick).catch(() => [])
          adminJobs.groups = getGroups().then(pick).catch(() => [])
          adminJobs.learners = getLearners({ status: '1' }).then(pick).catch(() => [])
          if (isMaster) adminJobs.admins = getAdminUsers({ status: 'active' }).then(pick).catch(() => [])
        }

        if (isCreator && !isLearner) {
          creatorJobs.myCourses = getMyCourses({ status: 'active' }).then(pick).catch(() => [])
          creatorJobs.subjects = getSubjects().then(pick).catch(() => [])
          creatorJobs.examBank = fetchExamBank({ page: 1, pageSize: 1 }).then(r => resolveTotal(r?.data)).catch(() => 0)
          if (!isAdmin) {
            creatorJobs.tests = getTests({}).then(pick).catch(() => [])
          }
        }

        // Wait for all jobs
        const adminKeys = Object.keys(adminJobs)
        const creatorKeys = Object.keys(creatorJobs)
        
        const [adminRes, creatorRes] = await Promise.all([
          Promise.all(Object.values(adminJobs)),
          Promise.all(Object.values(creatorJobs))
        ])

        if (!alive) return

        const adminData = Object.fromEntries(adminKeys.map((k, i) => [k, adminRes[i]]))
        const creatorData = Object.fromEntries(creatorKeys.map((k, i) => [k, creatorRes[i]]))

        if (isAdmin) {
          const t = adminData.tests || []
          setTests((t || []).filter(x => isUUID(x?.id || x?.test_id)))
          setCourses(adminData.courses || [])
          setClasses(adminData.classes || [])
          setGroups(adminData.groups || [])
          setLearners(adminData.learners || [])
          setAdmins(adminData.admins || [])
        }

        if (isCreator) {
          const myc = creatorData.myCourses || []
          const sbj = creatorData.subjects || []
          const bank = creatorData.examBank || 0
          const t2 = !isAdmin ? creatorData.tests : adminData.tests
          
          setMyCourses(myc)
          setSubjectsCount(Array.isArray(sbj) ? sbj.length : Number(sbj) || 0)
          setExamBankTotal(Number(bank) || 0)
          if (!isAdmin) {
            const finalTests = t2 || []
            setTests(finalTests.filter(x => isUUID(x?.id || x?.test_id)))
          }
        }
      } catch (err) {
        console.error("Dashboard load failed:", err)
      } finally {
        if (alive) setLoading(false)
      }
    }
    load()
    return () => { alive = false }
  }, [mode, roles])

  return (
    <div className="p-4 md:p-6 space-y-8">
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
        className="rounded-2xl bg-gradient-to-r from-primary/20 via-secondary/20 to-primary/20 p-6 shadow-md flex items-center gap-4">
        <div className="p-3 rounded-2xl bg-white shadow text-primary">
          {isAdmin ? (isMaster ? <Crown size={24} /> : <LayoutDashboard size={24} />) : <LayoutDashboard size={24} />}
        </div>
        <div>
          <div className="text-xl md:text-2xl font-bold text-gray-800">
            {isAdmin ? (isMaster ? 'แดชบอร์ด Master Admin' : 'แดชบอร์ด Admin') : 'แดชบอร์ด Creator'}
          </div>
          <div className="text-gray-500 text-sm">อัปเดต {new Date().toLocaleString()}</div>
        </div>
      </motion.div>

      {isAdmin && (
        <div className="flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 font-medium ${activeTab === 'overview' ? 'text-primary border-b-2 border-primary' : 'text-gray-500'}`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('mycontent')}
            className={`px-4 py-2 font-medium ${activeTab === 'mycontent' ? 'text-primary border-b-2 border-primary' : 'text-gray-500'}`}
          >
            My Content
          </button>
        </div>
      )}

      {(!isAdmin || activeTab === 'mycontent') && (
        <section className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
            <StatCard icon={ClipboardList} label="ข้อสอบของฉัน" value={tests.length} />
            <StatCard icon={Library} label="หลักสูตรของฉัน" value={myCourses.length} />
            <StatCard icon={Book} label="เนื้อหา" value={subjectsCount} />
            <StatCard icon={BookOpenCheck} label="คลังข้อสอบ" value={examBankTotal} />
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <ListCard icon={ClipboardList} title="ข้อสอบล่าสุดของฉัน" items={tests.slice(0, 8)} empty={loading ? 'กำลังโหลด...' : 'ไม่มีข้อมูล'}
              onViewAll={() => nav('/tests')} renderItem={it => <RowTest nav={nav} it={it} isCreator />} />
            <ListCard icon={Library} title="หลักสูตรของฉัน" items={myCourses.slice(0, 8)} empty={loading ? 'กำลังโหลด...' : 'ไม่มีข้อมูล'}
              onViewAll={() => nav('/my-courses')}
              renderItem={it => (
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{it?.name ?? it?.course_name ?? 'ไม่ระบุชื่อ'}</div>
                    <div className="text-xs text-gray-500">สถานะ {it?.active ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}</div>
                  </div>
                  <button onClick={() => nav('/my-courses')} className="text-primary text-sm hover:underline shrink-0">เปิดดู</button>
                </div>
              )} />
          </div>
        </section>
      )}

      {isAdmin && activeTab === 'overview' && (
        <section className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <StatCard icon={User} label="ผู้เรียน" value={learners.length} />
            <StatCard icon={Users} label="กลุ่มผู้เรียน" value={groups.length} />
            <StatCard icon={GraduationCap} label="ห้องเรียน" value={classes.length} />
            <StatCard icon={Library} label="หลักสูตร" value={courses.length} />
            <StatCard icon={ClipboardList} label="ชุดข้อสอบ" value={tests.length} />
            {isMaster && <StatCard icon={Crown} label="ผู้ดูแลระบบ" value={admins.length} />}
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <ListCard icon={ClipboardList} title="ชุดข้อสอบล่าสุด" items={tests.slice(0, 8)} empty={loading ? 'กำลังโหลด...' : 'ไม่มีข้อมูล'}
              onViewAll={() => nav('/tests')} renderItem={it => <RowTest nav={nav} it={it} />} />
            <ListCard icon={GraduationCap} title="ห้องเรียนล่าสุด" items={classes.slice(0, 8)} empty={loading ? 'กำลังโหลด...' : 'ไม่มีข้อมูล'}
              onViewAll={() => nav('/class')} renderItem={it => <RowClass nav={nav} it={it} />} />
            <ListCard icon={Library} title="หลักสูตรล่าสุด" items={courses.slice(0, 8)} empty={loading ? 'กำลังโหลด...' : 'ไม่มีข้อมูล'}
              onViewAll={() => nav('/course')} renderItem={it => <RowCourse nav={nav} it={it} />} />
            <ListCard icon={Users} title="กลุ่มผู้เรียนล่าสุด" items={groups.slice(0, 8)} empty={loading ? 'กำลังโหลด...' : 'ไม่มีข้อมูล'}
              onViewAll={() => nav('/group')} renderItem={it => <RowGroup nav={nav} it={it} />} />
          </div>
        </section>
      )}
    </div>
  )
}

function RowTest({ nav, it, isCreator }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="font-medium truncate">{it?.name ?? it?.test_name ?? 'ไม่ระบุชื่อ'}</div>
        <div className="text-xs text-gray-500">สถานะ {it?.active ? 'เปิด' : 'ปิด'}</div>
      </div>
      <button onClick={() => nav('/tests')} className="text-primary text-sm hover:underline shrink-0">
        {isCreator ? 'แก้ไข' : 'จัดการ'}
      </button>
    </div>
  )
}
function RowClass({ nav, it }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="font-medium truncate">{it?.name ?? it?.class_name ?? 'ไม่ระบุชื่อ'}</div>
        <div className="text-xs text-gray-500">สมาชิก {it?.member_count ?? it?.members?.length ?? 0} คน</div>
      </div>
      <button onClick={() => nav('/class')} className="text-primary text-sm hover:underline shrink-0">จัดการ</button>
    </div>
  )
}
function RowCourse({ nav, it }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="font-medium truncate">{it?.name ?? it?.course_name ?? 'ไม่ระบุชื่อ'}</div>
        <div className="text-xs text-gray-500">สถานะ {it?.active ? 'เปิด' : 'ปิด'}</div>
      </div>
      <button onClick={() => nav('/course')} className="text-primary text-sm hover:underline shrink-0">จัดการ</button>
    </div>
  )
}
function RowGroup({ nav, it }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="font-medium truncate">{it?.name ?? 'ไม่ระบุชื่อ'}</div>
        <div className="text-xs text-gray-500">สมาชิก {it?.member_count ?? 0} คน</div>
      </div>
      <button onClick={() => nav('/group')} className="text-primary text-sm hover:underline shrink-0">จัดการ</button>
    </div>
  )
}
